// SignUpPage.jsx (updated with Next button)
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import httpClient from "../Api/axios";

// Utility function to convert File to Base64
const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        if (!file) {
            resolve(null);
            return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(reader.result.split(',')[1]);
        };
        reader.onerror = (error) => reject(error);
    });
};

// Icons
const EyeIcon = ({ onClick }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" onClick={onClick} style={styles.eyeIcon}>
        <path d="M12 4.5c-4.418 0-8 3.582-8 8s3.582 8 8 8 8-3.582 8-8-3.582-8-8-8zM12 18.5c-3.59 0-6.5-2.91-6.5-6.5S8.41 5.5 12 5.5s6.5 2.91 6.5 6.5-2.91 6.5-6.5 6.5z" />
        <path d="M12 11.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
    </svg>
);

const EyeSlashIcon = ({ onClick }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" onClick={onClick} style={styles.eyeIcon}>
        <path d="M12 4.5c-4.418 0-8 3.582-8 8s3.582 8 8 8 8-3.582 8-8-3.582-8-8-8zM12 18.5c-3.59 0-6.5-2.91-6.5-6.5S8.41 5.5 12 5.5s6.5 2.91 6.5 6.5-2.91 6.5-6.5 6.5z" />
        <path d="M21 12c0 2.485-2.239 4.5-5 4.5s-5-2.015-5-4.5c0-1.285.5-2.455 1.353-3.336L21 12zM3 12c0-2.485 2.239-4.5 5-4.5s5 2.015 5 4.5c0 1.285-.5 2.455-1.353 3.336L3 12z" clipRule="evenodd" />
        <path d="M12 11.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
    </svg>
);

const ErrorMessage = ({ message, onClose }) => {
    if (!message) return null;
    
    React.useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                if (onClose) onClose();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message, onClose]);
    
    return (
        <div style={styles.errorPopup}>
            <div style={styles.errorContent}>
                <span style={styles.errorIcon}>⚠️</span>
                <span style={styles.errorText}>{message}</span>
                <button onClick={onClose} style={styles.errorCloseBtn}>×</button>
            </div>
        </div>
    );
};

function SignUpPage() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        userEmail: "",
        userName: "",
        password: "",
        confirmPassword: "",
        userDOB: "",
        userGender: "",
        userRole: "",
        collegeName: "",
        userProfile: "",
        currentlocation: "",
        countryPhoneCode: "",
        phoneNumber: "",
        reference: "",
        areaOfInterest: "",
        remarks: ""
    });

    const [profilePhoto, setProfilePhoto] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [usernameAvailability, setUsernameAvailability] = useState(null);
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [emailAvailability, setEmailAvailability] = useState(null);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [touchedFields, setTouchedFields] = useState({});

    // Check username availability with debounce
    useEffect(() => {
        if (formData.userName && formData.userName.length > 2) {
            setUsernameAvailability(null);
            const handler = setTimeout(() => {
                checkUsernameAvailability(formData.userName);
            }, 500);
            return () => clearTimeout(handler);
        }
    }, [formData.userName]);

    // Check email availability with debounce
    useEffect(() => {
        if (formData.userEmail && formData.userEmail.length > 5 && formData.userEmail.includes('@')) {
            setEmailAvailability(null);
            const handler = setTimeout(() => {
                checkEmailAvailability(formData.userEmail);
            }, 500);
            return () => clearTimeout(handler);
        }
    }, [formData.userEmail]);

    const checkUsernameAvailability = async (username) => {
        setIsCheckingUsername(true);
        try {
            const response = await httpClient.get(`/api/signup/check-username?username=${username}`);
            if (response.data.available) {
                setUsernameAvailability(true);
            } else {
                setUsernameAvailability(false);
            }
        } catch (err) {
            console.error("Error checking username:", err);
            setUsernameAvailability(null);
        }
        setIsCheckingUsername(false);
    };

    const checkEmailAvailability = async (email) => {
        setIsCheckingEmail(true);
        try {
            const response = await httpClient.get(`/api/signup/check-email?email=${email}`);
            if (response.data.available) {
                setEmailAvailability(true);
            } else {
                setEmailAvailability(false);
            }
        } catch (err) {
            console.error("Error checking email:", err);
            setEmailAvailability(null);
        }
        setIsCheckingEmail(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (name === 'userName') {
            setUsernameAvailability(null);
        }
        if (name === 'userEmail') {
            setEmailAvailability(null);
        }
    };

    const handleBlur = (fieldName) => {
        setTouchedFields({ ...touchedFields, [fieldName]: true });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePhoto(file);
        }
    };

    const validateDOB = (dob) => {
        if (!dob) return { valid: true, message: "" };
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        if (age > 120) {
            return { valid: false, message: "Please enter a valid date of birth." };
        }
        return { valid: true, message: "" };
    };

    const validateField = (fieldName, value) => {
        switch(fieldName) {
            case 'userEmail':
                if (!value) return "Email is required.";
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) return "Please enter a valid email address.";
                if (emailAvailability === false) return "Email is already registered.";
                return "";
            case 'userName':
                if (!value) return "Profilename is required.";
                if (value.includes(" ")) return "Profilename cannot contain spaces.";
                if (usernameAvailability === false) return "Profilename is already taken.";
                return "";
            case 'password':
                if (!value) return "Password is required.";
                if (value.length < 6) return "Password must be at least 6 characters.";
                return "";
            case 'confirmPassword':
                if (!value) return "Please confirm your password.";
                if (value !== formData.password) return "Passwords do not match!";
                return "";
            case 'userRole':
                if (!value) return "User Role is required.";
                return "";
            case 'collegeName':
                if (!value) return "College/Organization name is required.";
                return "";
            case 'phoneNumber':
                if (!value) return "Phone number is required.";
                const phoneRegex = /^[0-9]{7,15}$/;
                if (!phoneRegex.test(value)) return "Please enter a valid phone number (7-15 digits).";
                return "";
            default:
                return "";
        }
    };

    const validateForm = () => {
        const fieldsToValidate = ['userEmail', 'userName', 'password', 'confirmPassword', 'userRole', 'collegeName', 'phoneNumber'];
        for (const field of fieldsToValidate) {
            const error = validateField(field, formData[field]);
            if (error) {
                setErrorMessage(error);
                setTimeout(() => {
                    const errorElement = document.querySelector('[data-error-popup]');
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 100);
                return false;
            }
        }
        const dobValidation = validateDOB(formData.userDOB);
        if (!dobValidation.valid) {
            setErrorMessage(dobValidation.message);
            return false;
        }
        return true;
    };

    const handleNext = async (e) => {
        e.preventDefault();
        
        setErrorMessage("");
        setSuccessMessage("");
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSuccessMessage("Creating your account...");

        let base64ProfilePhoto = null;
        if (profilePhoto) {
            try {
                base64ProfilePhoto = await convertFileToBase64(profilePhoto);
            } catch (err) {
                console.error("Error converting photo:", err);
                setErrorMessage("Failed to process profile photo.");
                setIsSubmitting(false);
                setSuccessMessage("");
                return;
            }
        }

        try {
            const dataToSend = {
                userEmail: formData.userEmail,
                userName: formData.userName,
                password: formData.password,
                userDOB: formData.userDOB ? new Date(formData.userDOB).toISOString() : null,
                userGender: formData.userGender || null,
                userRole: formData.userRole,
                collegeName: formData.collegeName,
                userProfile: base64ProfilePhoto || "default-profile",
                currentlocation: formData.currentlocation || null,
                countryPhoneCode: formData.countryPhoneCode || null,
                phoneNumber: formData.phoneNumber,
                reference: formData.reference || null,
                areaOfInterest: formData.areaOfInterest || null,
                remarks: formData.remarks || null
            };

            const response = await httpClient.post("/api/v1/signups/", dataToSend, {
                headers: { "Content-Type": "application/json" },
            });

            setSuccessMessage("Account created! Redirecting to subscription...");
            
            setTimeout(() => {
                navigate("/subscription", { 
                    state: { 
                        userId: response.data.signUpId,
                        userEmail: response.data.userEmail,
                        userName: response.data.userName
                    } 
                });
            }, 1500);

        } catch (err) {
            console.error("Signup error:", err);
            let errorMsg = "Sign Up Failed! Please try again.";
            if (err.response) {
                if (err.response.status === 409) {
                    errorMsg = err.response.data || "Username or Email already exists.";
                } else if (err.response.status === 400) {
                    errorMsg = "Validation failed: " + (err.response.data || "Bad Request.");
                } else {
                    errorMsg = `Sign Up failed (Status ${err.response.status}).`;
                }
            } else if (err.request) {
                errorMsg = "Network error: Could not connect to the server.";
            }
            setErrorMessage(errorMsg);
            setSuccessMessage("");
        } finally {
            setIsSubmitting(false);
        }
    };

    const today = new Date().toISOString().split("T")[0];

    return (
        <div style={styles.signupPage}>
            <div data-error-popup>
                <ErrorMessage message={errorMessage} onClose={() => setErrorMessage("")} />
            </div>
            
            <div style={styles.brandName}>CommunityofHubs.</div>
            <div style={styles.signupCard}>
                <h2 style={styles.title}>Sign Up</h2>
                <p style={styles.subtitle}>Join our community today!</p>
                
                {successMessage && (
                    <div style={styles.successMessage}>
                        {successMessage}
                    </div>
                )}
                
                <form onSubmit={handleNext} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Email Address *</label>
                        <input
                            type="email"
                            name="userEmail"
                            value={formData.userEmail}
                            onChange={handleChange}
                            onBlur={() => handleBlur('userEmail')}
                            style={styles.input}
                            placeholder="Enter your email"
                            required
                        />
                        {isCheckingEmail && <p style={styles.checkingMessage}>Checking email availability...</p>}
                        {emailAvailability === true && touchedFields.userEmail && (
                            <p style={styles.usernameAvailable}>✓ Email is available!</p>
                        )}
                        {emailAvailability === false && touchedFields.userEmail && (
                            <p style={styles.usernameTaken}>✗ Email is already registered</p>
                        )}
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Profilename *</label>
                        <input
                            type="text"
                            name="userName"
                            value={formData.userName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('userName')}
                            style={styles.input}
                            placeholder="Enter unique profilename (no spaces)"
                            required
                        />
                        {isCheckingUsername && <p style={styles.checkingMessage}>Checking availability...</p>}
                        {usernameAvailability === true && touchedFields.userName && (
                            <p style={styles.usernameAvailable}>✓ Profilename is available!</p>
                        )}
                        {usernameAvailability === false && touchedFields.userName && (
                            <p style={styles.usernameTaken}>✗ Profilename is already taken</p>
                        )}
                    </div>

                    <div style={styles.passwordRow}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Password *</label>
                            <div style={styles.passwordContainer}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('password')}
                                    style={styles.input}
                                    placeholder="Enter password (min 6 characters)"
                                    required
                                />
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Confirm Password *</label>
                            <div style={styles.passwordContainer}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('confirmPassword')}
                                    style={styles.input}
                                    placeholder="Confirm your password"
                                    required
                                />
                                {showPassword ? (
                                    <EyeSlashIcon onClick={() => setShowPassword(false)} />
                                ) : (
                                    <EyeIcon onClick={() => setShowPassword(true)} />
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={styles.row}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Date of Birth (Optional)</label>
                            <input
                                type="date"
                                name="userDOB"
                                value={formData.userDOB}
                                max={today}
                                onChange={handleChange}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Gender (Optional)</label>
                            <select
                                name="userGender"
                                value={formData.userGender}
                                onChange={handleChange}
                                style={styles.input}
                            >
                                <option value="">Select Gender (Optional)</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>User Role *</label>
                        <select
                            name="userRole"
                            value={formData.userRole}
                            onChange={handleChange}
                            onBlur={() => handleBlur('userRole')}
                            style={styles.input}
                            required
                        >
                            <option value="">Select Role</option>
                            <option value="Student">Student</option>
                            <option value="Software Engineer">Software Engineer</option>
                            <option value="Data Scientist">Data Scientist</option>
                            <option value="Product Manager">Product Manager</option>
                            <option value="Designer">Designer</option>
                            <option value="Researcher">Researcher</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>College/Organization Name *</label>
                        <input
                            type="text"
                            name="collegeName"
                            value={formData.collegeName}
                            onChange={handleChange}
                            onBlur={() => handleBlur('collegeName')}
                            style={styles.input}
                            placeholder="Enter your college or organization name"
                            required
                        />
                    </div>

                    <div style={styles.row}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Country Code (Optional)</label>
                            <input
                                type="text"
                                name="countryPhoneCode"
                                value={formData.countryPhoneCode}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="e.g., +1, +44, +91, etc."
                            />
                            <small style={styles.helperText}>Enter your country code (e.g., +1 for USA, +44 for UK)</small>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Phone Number *</label>
                            <input
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                onBlur={() => handleBlur('phoneNumber')}
                                style={styles.input}
                                placeholder="Enter phone number"
                                required
                            />
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Current Location (Optional)</label>
                        <input
                            type="text"
                            name="currentlocation"
                            value={formData.currentlocation}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="e.g., United States, India, UK"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>How did you hear about us? (Optional)</label>
                        <input
                            type="text"
                            name="reference"
                            value={formData.reference}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="e.g., Friend Referral, Google Search, Social Media, LinkedIn, etc."
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Area of Interest (Optional)</label>
                        <input
                            type="text"
                            name="areaOfInterest"
                            value={formData.areaOfInterest}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="e.g., Machine Learning, Web Development, Cloud Computing, etc."
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Any Query / Remarks (Optional)</label>
                        <textarea
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            style={styles.textarea}
                            placeholder="Please share any questions, feedback, or remarks you have..."
                            rows="4"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Profile Photo (Optional)</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={styles.input}
                        />
                        <small style={styles.helperText}>Upload a profile picture (JPG, PNG)</small>
                    </div>

                    <button
                        type="submit"
                        style={styles.submitBtn}
                        disabled={isSubmitting}
                        onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = "#555")}
                        onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = "#1a1a1a")}
                    >
                        {isSubmitting ? "Creating Account..." : "Next"}
                    </button>
                </form>

                <p style={styles.signInLink}>
                    Already have an account?{" "}
                    <span onClick={() => navigate("/signin")} style={styles.linkText}>
                        Sign In
                    </span>
                </p>
            </div>
        </div>
    );
}

const styles = {
    signupPage: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#121212",
        padding: "20px",
        position: "relative",
    },
    errorPopup: {
        position: "fixed",
        top: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        width: "90%",
        maxWidth: "500px",
        animation: "slideDown 0.3s ease-out",
    },
    errorContent: {
        backgroundColor: "#dc3545",
        color: "white",
        padding: "12px 20px",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    },
    errorIcon: {
        fontSize: "20px",
    },
    errorText: {
        flex: 1,
        fontSize: "14px",
    },
    errorCloseBtn: {
        background: "none",
        border: "none",
        color: "white",
        fontSize: "24px",
        cursor: "pointer",
        padding: "0 5px",
        lineHeight: "1",
    },
    successMessage: {
        backgroundColor: "#28a745",
        color: "white",
        padding: "12px 20px",
        borderRadius: "8px",
        marginBottom: "20px",
        textAlign: "center",
        animation: "slideDown 0.3s ease-out",
    },
    brandName: {
        fontSize: "2rem",
        fontWeight: "bold",
        color: "#FFFFFF",
        marginBottom: "1rem",
        textAlign: "center",
    },
    signupCard: {
        width: "100%",
        maxWidth: "700px",
        padding: "2rem",
        backgroundColor: "#000",
        borderRadius: "15px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
        color: "white",
    },
    title: {
        textAlign: "center",
        fontWeight: "bold",
        fontSize: "1.8rem",
        marginBottom: "0.5rem",
    },
    subtitle: {
        textAlign: "center",
        fontSize: "1rem",
        marginBottom: "1.5rem",
        color: "#ccc",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    formGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1rem",
    },
    passwordRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1rem",
    },
    label: {
        fontWeight: "500",
        fontSize: "0.9rem",
        color: "#fff",
    },
    input: {
        padding: "0.75rem",
        border: "2px solid #333",
        borderRadius: "8px",
        backgroundColor: "#111",
        color: "white",
        outline: "none",
        fontSize: "1rem",
        transition: "border-color 0.3s ease",
        width: "100%",
    },
    textarea: {
        padding: "0.75rem",
        border: "2px solid #333",
        borderRadius: "8px",
        backgroundColor: "#111",
        color: "white",
        outline: "none",
        fontSize: "1rem",
        transition: "border-color 0.3s ease",
        fontFamily: "inherit",
        resize: "vertical",
    },
    passwordContainer: {
        position: "relative",
        width: "100%",
    },
    eyeIcon: {
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        width: "20px",
        height: "20px",
        color: "#00bfff",
        cursor: "pointer",
    },
    usernameAvailable: {
        color: "#28a745",
        fontSize: "0.8rem",
        marginTop: "0.25rem",
    },
    usernameTaken: {
        color: "#dc3545",
        fontSize: "0.8rem",
        marginTop: "0.25rem",
    },
    checkingMessage: {
        color: "#ffc107",
        fontSize: "0.8rem",
        marginTop: "0.25rem",
    },
    helperText: {
        fontSize: "0.75rem",
        color: "#888",
        marginTop: "0.25rem",
    },
    submitBtn: {
        marginTop: "1rem",
        padding: "0.75rem",
        border: "none",
        backgroundColor: "#1a1a1a",
        color: "white",
        cursor: "pointer",
        borderRadius: "8px",
        fontSize: "1rem",
        fontWeight: "bold",
        transition: "background-color 0.3s ease",
    },
    signInLink: {
        marginTop: "1.5rem",
        fontSize: "0.9rem",
        color: "#aaa",
        textAlign: "center",
    },
    linkText: {
        color: "#00bfff",
        cursor: "pointer",
        textDecoration: "underline",
    },
};

const animationStyles = `
    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
        }
        to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
        }
    }
    
    @media (max-width: 768px) {
        .row, .passwordRow {
            grid-template-columns: 1fr;
            gap: 1rem;
        }
    }
    
    @media (max-width: 480px) {
        .signupCard {
            padding: 1rem;
            background-color: transparent;
            box-shadow: none;
        }
        .brandName {
            font-size: 1.5rem;
        }
        .title {
            font-size: 1.3rem;
        }
        .input, .textarea {
            font-size: 0.9rem;
            padding: 0.6rem;
        }
    }
`;

if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = animationStyles;
    document.head.appendChild(styleSheet);
}

export default SignUpPage;